'use client'
import * as React from 'react'
import { Button } from '@repo/design-system-cf/components/button'
import { IconButton } from 'primitives/buttons/icon-button'
import { formatUserName } from 'utils/format-user-name'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { tw } from 'utils/classnames'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { CloseIcon } from '@web/app/[locale]/clubhouse/assets/icons'
import {
  useClubhousesAcceptInviteMutation,
  useClubhouseListPendingInvitationsQuery,
} from '@web/gen/clubhouses'
import { LFS_URL } from '@web/utils/env'
import type { Clubhouse } from 'codegen/types'

export function InviteBanner({ clubhouse }: { clubhouse?: Clubhouse | null }) {
  const t = useTranslations('pages.clubhouse')
  const queryClient = useQueryClient()
  const user = useUser()
  const { breakpoint } = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')
  const [isShown, setIsShown] = React.useState(false)
  const clubhousesAcceptInviteMutation = useClubhousesAcceptInviteMutation()
  const { data: pendingInvitations } = useClubhouseListPendingInvitationsQuery(
    {
      input: {
        clubhouseSlug: clubhouse?.slug ?? '',
        itemsPerPage: 30,
        page: 1,
      },
    },
    {
      select: data => data?.clubhouseListPendingInvitations,
      placeholderData: keepPreviousData,
    },
  )

  const inviteFriend = pendingInvitations?.pendingInviters?.[0]
  const userAvatars = pendingInvitations?.pendingInviters?.map((user, index) => {
    return {
      avatarUrl: user.avatarUrl,
      slug: user.slug,
      id: user.id,
      index: index,
    }
  })

  function handleAcceptInventation() {
    clubhousesAcceptInviteMutation.mutate(
      {
        input: {
          clubhouseSlug: clubhouse?.slug ?? '',
        },
      },
      {
        onSuccess: data => {
          if (data?.clubhousesAcceptInvite.success) {
            setIsShown(false)
            queryClient.invalidateQueries({
              queryKey: ['clubhousesIsMember', { input: { clubhouseSlug: clubhouse?.slug } }],
            })
            queryClient.invalidateQueries({
              queryKey: ['clubhousesFromUser', { input: { userId: user.data?.id } }],
            })
          }
        },
      },
    )
  }

  React.useEffect(() => {
    if (pendingInvitations?.pendingInviters?.length) {
      setIsShown(true)
    } else {
      setIsShown(false)
    }
  }, [pendingInvitations])

  return isShown ? (
    <div
      style={{
        // oxlint-disable-next-line no-negated-condition
        backgroundImage: !isMobile
          ? `url(${LFS_URL}/web/pages/clubhouses/join-banner.png)`
          : undefined,
      }}
      className="bg-cf-purple-50 relative mb-3 min-h-[160px] overflow-hidden rounded-[12px] border border-[#0A0A0B1A] bg-contain bg-[position:right_5%_bottom_45%] bg-no-repeat px-4 py-6 lg:mb-6 lg:p-6"
    >
      <Image
        width={200}
        height={114}
        alt="Clubhouse Join Banner"
        className="absolute bottom-0 right-0 md:hidden"
        src={`${LFS_URL}/web/pages/clubhouses/join-banner-mobile.png`}
      />
      <div className="flex h-full flex-col items-start justify-center gap-3">
        {userAvatars?.map((user, index) =>
          user.avatarUrl ? (
            <Link
              href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${user.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              key={user.id}
              className={tw(
                `bg-cf-white -ml-4 size-7 overflow-hidden rounded-full outline outline-offset-0 outline-[#0A0A0B1A] hover:outline-[#0A0A0B1A]`,
                index === 0 && 'ml-0',
              )}
            >
              <Image src={user.avatarUrl} alt="User Avatar" width={32} height={32} />
            </Link>
          ) : null,
        )}
        <p className="text-20 max-w-96 font-semibold xl:max-w-full">
          {t.rich('clubhouse_homepage.invite_banner_text', {
            fullName: formatUserName(inviteFriend?.firstName, inviteFriend?.lastName, 'User'),
            clubName: clubhouse?.name || '',
          })}
        </p>
        <Button className="w-[126px]" onClick={handleAcceptInventation}>
          {t('clubhouse_homepage.invite_banner_button_text')}
        </Button>
      </div>
      <IconButton
        onClick={() => setIsShown(false)}
        className="absolute right-2 top-2 size-8 bg-transparent text-[#4B5563] hover:bg-transparent focus:bg-transparent focus:ring-0 focus-visible:outline-none active:bg-transparent active:text-[#4B5563]"
      >
        <CloseIcon className="size-6" />
      </IconButton>
    </div>
  ) : null
}
